// Theme (light/dark). Account-bound with a device fallback: the signed-in user's choice
// lives in their server settings (key "theme", so it follows them to any workstation),
// while the device localStorage copy ("healo-theme") serves the pre-login lock screen and
// mirrors whatever the last user applied — App's theme effect keeps it in sync on every
// theme change, including login-driven ones. A fresh account with no stored theme keeps
// showing the device theme and uploads nothing until the user explicitly picks one.

import {
  USER_SETTING_KEYS,
  getActiveSettingsUid,
  readUserSetting,
  subscribeUserSettingsActivation,
  writeUserSetting,
} from "@/lib/stores/user-settings-sync";

const DEVICE_THEME_STORAGE_KEY = "healo-theme";
const THEME_CHANGE_EVENT = "healo-theme-change";

let activationSubscribed = false;

function canUseBrowserStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function normalizeTheme(value) {
  return value === "light" || value === "dark" ? value : "";
}

function readDeviceTheme() {
  if (!canUseBrowserStorage()) return "";
  try {
    return normalizeTheme(window.localStorage.getItem(DEVICE_THEME_STORAGE_KEY));
  } catch {
    return "";
  }
}

function systemTheme() {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function emitChange() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
}

// An identity swap can change the effective theme (each account carries its own), so
// re-announce on every activation — subscribers re-read getTheme().
function ensureActivationSubscription() {
  if (activationSubscribed || typeof window === "undefined") return;
  activationSubscribed = true;
  subscribeUserSettingsActivation(emitChange);
}

/** Effective theme: the account's stored choice, else the device copy, else the OS. */
export function getTheme() {
  ensureActivationSubscription();
  if (getActiveSettingsUid()) {
    const accountTheme = normalizeTheme(readUserSetting(USER_SETTING_KEYS.THEME)?.value);
    if (accountTheme) return accountTheme;
  }
  return readDeviceTheme() || systemTheme();
}

/** Apply a theme: device copy always (lock-screen mirror), account when signed in. */
export function setTheme(next) {
  const theme = normalizeTheme(next);
  if (!theme) return getTheme();
  if (canUseBrowserStorage()) {
    try {
      window.localStorage.setItem(DEVICE_THEME_STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }
  if (getActiveSettingsUid()) {
    writeUserSetting(USER_SETTING_KEYS.THEME, { value: theme });
  }
  emitChange();
  return theme;
}

export function toggleTheme() {
  return setTheme(getTheme() === "dark" ? "light" : "dark");
}

export function subscribeTheme(listener) {
  if (typeof window === "undefined" || typeof listener !== "function") {
    return () => {};
  }
  ensureActivationSubscription();
  const handler = () => listener(getTheme());
  window.addEventListener(THEME_CHANGE_EVENT, handler);
  return () => {
    window.removeEventListener(THEME_CHANGE_EVENT, handler);
  };
}
